import { v4 } from "uuid";

import {
  AI_NODE_V2_INSIGHTS,
  AiNodeJsonSchemaBE,
  AiNodeJsonSchemaPropertyUI,
  AiNodeJsonSchemaUI,
  AiNodeV2Form,
  AiNodeV2Insight,
  AiNodeV2MessageContentBlock,
  AiNodeV2MetaFixed,
} from "src/aiNode/types";
import { ResourceSample } from "src/api/connectApi/types";
import {
  AiNodeV2ConversationMessageFilesListContentBlockBlockTypeEnum,
  OutputAttribute,
} from "src/clients/flow-api";
import { AiNodeV2 } from "src/constants/NodeDataTypes";
import { generateSampleReportForSchema } from "src/datasets/utils";
import { SchemaOptions } from "src/router/SearchParams";
import { defaultSchema, SchemaConverter } from "src/schema/schemaMappingUtils";

// The "text" output and insights which we don't want to cleanup
// when processing JSON schemas
const PROTECTED_RESPONSE_NAMES = ["default", "provider_response_time"];
const LARGE_NUMBER_SUFFIXES = ["", "k", "m", "b", "t"];

export const getAiNodeNewProperty = (): AiNodeJsonSchemaPropertyUI => {
  return {
    fieldName: "",
    type: ["string", "null"],
    required: true,
    sensitive: false,
    outputId: v4(),
    description: "",
  };
};

const getDefaultFilesListContentBlock = () => ({
  block_type:
    AiNodeV2ConversationMessageFilesListContentBlockBlockTypeEnum.FILES_LIST,
  files: {
    id: v4(),
    active: false,
    expression: "",
  },
});

export const convertAiNodeJsonSchemaBETtoUIT = (
  schemaBE: AiNodeJsonSchemaBE,
): AiNodeJsonSchemaUI => {
  const schemaUI = SchemaConverter.beToUI(schemaBE, SchemaOptions.Output);

  return {
    ...schemaUI,
    properties: schemaUI.properties.map((property) => ({
      ...property,
      description: schemaBE.properties[property.fieldName].description ?? "",
      // Backfilling the outputId in case it's missing so we can regenerate
      // the response fields down the line
      outputId: schemaBE.properties[property.fieldName].outputId ?? v4(),
    })),
  };
};

export const convertAiNodeJsonSchemaUITtoBET = (
  schemaUI: AiNodeJsonSchemaUI,
): AiNodeJsonSchemaBE => {
  const schemaBE = SchemaConverter.uiToBE(schemaUI, SchemaOptions.Output);
  const propertiesMappingUI = Object.fromEntries(
    schemaUI.properties.map((property: AiNodeJsonSchemaPropertyUI) => [
      property.fieldName,
      {
        outputId: property.outputId,
        description: property.description,
      },
    ]),
  );
  return {
    ...schemaBE,
    properties: Object.fromEntries(
      Object.entries(schemaBE.properties).map(([fieldName, property]) => [
        fieldName,
        {
          ...property,
          outputId: propertiesMappingUI[fieldName].outputId,
          description: propertiesMappingUI[fieldName]?.description,
        },
      ]),
    ),
  };
};

export const convertAiNodeV2FormToBE = (
  form: AiNodeV2Form,
): AiNodeV2MetaFixed => {
  const uiSchema = form.inference.response_format.json_schema;
  if (!uiSchema) {
    return {
      ...form,
      inference: {
        ...form.inference,
        response_format: {
          ...form.inference.response_format,
          json_schema: undefined,
        },
      },
    };
  }

  // JSONSchema is a source of truth for the response fields, therefore
  // we re-create the response object based on the schemas

  const backendSchema = convertAiNodeJsonSchemaUITtoBET(uiSchema);
  // Keep protected response field names (text output and insights)
  const response = Object.fromEntries(
    Object.entries(form.response).filter(([key]) =>
      PROTECTED_RESPONSE_NAMES.includes(key),
    ),
  );

  // Add output fields from schema
  Object.entries(backendSchema.properties).forEach(([fieldName, property]) => {
    response[fieldName] = {
      // We are backfilling the `outputId` if it's missing when parsing BE
      // data, so `outputId` will always be set at this point
      id: property.outputId!,
      active: true,
      mapped_to: fieldName,
    };
  });
  return {
    ...form,
    response,
    inference: {
      ...form.inference,
      response_format: {
        ...form.inference.response_format,
        json_schema: backendSchema,
      },
    },
  };
};

/**
 * Backfills the files_list content block in the messages if it's missing.
 * This is needed to make sure we don't need to handle the missing files_list
 * case down the line.
 */
const backfillFilesListInMessages = (
  messages: AiNodeV2MetaFixed["prompts"]["messages"],
) => {
  return messages.map((message) => {
    const content = message.content as AiNodeV2MessageContentBlock[];

    const hasFilesList = content.some(
      (c) =>
        c.block_type ===
        AiNodeV2ConversationMessageFilesListContentBlockBlockTypeEnum.FILES_LIST,
    );

    if (!hasFilesList) {
      // Add a files_list content block to the list of content blocks
      // if it's missing
      content.push(getDefaultFilesListContentBlock());
    }

    return {
      ...message,
      content,
    };
  });
};

const backfillInsights = (
  insights: AiNodeV2MetaFixed["insights"],
): Record<AiNodeV2Insight, OutputAttribute> => {
  const processedInsights = insights ?? {};

  AI_NODE_V2_INSIGHTS.forEach((insight) => {
    if (!processedInsights[insight]) {
      processedInsights[insight] = {
        id: v4(),
        active: false,
        mapped_to: "",
      };
    }
  });

  return processedInsights as Record<AiNodeV2Insight, OutputAttribute>;
};

export const convertAiNodeV2BEToForm = (
  data: AiNodeV2MetaFixed,
): AiNodeV2Form => {
  const backendSchema = (data.inference.response_format.json_schema ??
    structuredClone(defaultSchema)) as AiNodeJsonSchemaBE;

  const uiSchema = convertAiNodeJsonSchemaBETtoUIT(backendSchema);

  return {
    model: data.model,
    prompts: {
      ...data.prompts,
      messages: backfillFilesListInMessages(data.prompts.messages),
    },
    response: data.response,
    insights: backfillInsights(data.insights),
    inference: {
      ...data.inference,
      response_format: {
        ...data.inference.response_format,
        json_schema: uiSchema,
        original_json_schema: backendSchema,
      },
    },
    config: data.config,
  };
};

export const getSampleReportsForAiNode = (node: AiNodeV2): ResourceSample[] => {
  const insightsSample = Object.fromEntries(
    AI_NODE_V2_INSIGHTS.map((insight) => [insight, 0]),
  );

  if (node.data.inference.response_format.type === "text") {
    return [
      {
        name: "AI Node response",
        sample: { text: "example", ...insightsSample },
      },
    ];
  }

  const schema = node.data.inference.response_format
    .json_schema as AiNodeJsonSchemaBE;
  if (!schema) {
    return [
      { name: "AI Node response", sample: { json: {}, ...insightsSample } },
    ];
  }

  const uiSchema = convertAiNodeJsonSchemaBETtoUIT(schema);

  const sampleReport = generateSampleReportForSchema({
    schema: uiSchema,
  });

  return [
    {
      name: "AI Node response",
      sample: { json: sampleReport, ...insightsSample },
    },
  ];
};

export const formatLargeNumbers = (num: number): string => {
  if (num < 1000) {
    return num.toString();
  }
  const suffixIndex = Math.floor(Math.log10(num) / 3);
  const scaleValue = Math.pow(10, suffixIndex * 3);

  // scale number to the appropriate amount
  const scaledNum = num / scaleValue;
  // We only want 2 decimal places if the scaled number
  // has less than 3 digits
  const formatted =
    scaledNum < 100
      ? parseFloat(scaledNum.toFixed(2)).toString()
      : Math.round(scaledNum).toString();

  return `${formatted}${LARGE_NUMBER_SUFFIXES[suffixIndex]}`;
};
